import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const RentalActivity = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadRentalData();
  }, []);

  const loadRentalData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/rentals');
      setStats(response.data.stats || {
        totalRentals: 0,
        activeRentals: 0,
        completedRentals: 0,
        totalRevenue: 0
      });
      setRentals(response.data.rentals || []);
    } catch (error) {
      toast.error('Error loading rental data');
      // Fallback to empty data
      setStats({
        totalRentals: 0,
        activeRentals: 0,
        completedRentals: 0,
        totalRevenue: 0
      });
      setRentals([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: '#948979', text: 'white', icon: 'fa-clock' },
      completed: { bg: '#22c55e', text: 'white', icon: 'fa-check-circle' },
      overdue: { bg: '#dc2626', text: 'white', icon: 'fa-exclamation-triangle' },
      cancelled: { bg: '#393E46', text: 'white', icon: 'fa-times-circle' }
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span 
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-md"
        style={{ backgroundColor: config.bg, color: config.text }}
      >
        <i className={`fas ${config.icon} mr-1`}></i>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading rental activity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 lg:mb-8">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-exchange-alt text-white text-lg lg:text-xl"></i>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold" style={{ color: '#222831' }}>
              Rental Activity
            </h2>
          </div>
          <p className="ml-13 lg:ml-15 text-sm lg:text-base" style={{ color: '#393E46' }}>Track all rental transactions and activity</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Total Rentals</p>
              <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#222831' }}>{stats.totalRentals}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#222831' }}></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>All time</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-handshake text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Active Rentals</p>
              <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#948979' }}>{stats.activeRentals}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#948979' }}></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Currently ongoing</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-clock text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Completed</p>
              <p className="text-2xl lg:text-3xl font-bold text-green-600">{stats.completedRentals}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Successfully finished</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-green-500 rounded-2xl flex items-center justify-center shadow-lg">
              <i className="fas fa-check-circle text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Rental Revenue</p>
              <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#948979' }}>₹{stats.totalRevenue}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#948979' }}></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Platform earnings</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#393E46' }}>
              <i className="fas fa-rupee-sign text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Rental Transactions */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="p-6 border-b-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-list text-white text-sm"></i>
            </div>
            <h3 className="text-xl font-bold" style={{ color: '#222831' }}>Recent Rental Activity</h3>
          </div>
        </div>

        {rentals.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-exchange-alt text-4xl" style={{ color: '#948979' }}></i>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#222831' }}>No rental activity yet</h3>
            <p style={{ color: '#393E46' }}>Rental transactions will appear here once users start renting items</p>
          </div>
        ) : (
          <div className="divide-y-2" style={{ borderColor: '#DFD0B8' }}>
            {rentals.map((rental) => (
              <div key={rental.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Rental Icon */}
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold shadow-lg" style={{ backgroundColor: '#948979' }}>
                      <i className="fas fa-exchange-alt"></i>
                    </div>
                    
                    {/* Rental Details */}
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-bold text-lg" style={{ color: '#222831' }}>
                          {rental.itemName}
                        </h4>
                        {getStatusBadge(rental.status)}
                      </div>
                  <div className="flex items-center space-x-4 text-sm" style={{ color: '#393E46' }}>
                        <div className="flex items-center space-x-1">
                          <i className="fas fa-user" style={{ color: '#948979' }}></i>
                          <span>Renter: {rental.renter}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <i className="fas fa-user-tie" style={{ color: '#948979' }}></i>
                          <span>Owner: {rental.owner}</span>
                        </div>
                        {rental.renterLocation && (
                          <>
                            <span>•</span>
                            <div className="flex items-center space-x-1">
                              <i className="fas fa-map-marker-alt" style={{ color: '#948979' }}></i>
                              <span>{rental.renterLocation}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-xs mt-1" style={{ color: '#393E46' }}>
                        <span>{rental.startDate} to {rental.endDate}</span>
                        {rental.renterContact && (
                          <>
                            <span>•</span>
                            <div className="flex items-center space-x-1">
                              <i className="fas fa-phone" style={{ color: '#948979' }}></i>
                              <span>{rental.renterContact}</span>
                            </div>
                          </>
                        )}
                      </div>
                      {rental.notes && (
                        <div className="text-xs mt-2 p-2 rounded-lg" style={{ backgroundColor: '#DFD0B8', color: '#393E46' }}>
                          <i className="fas fa-sticky-note mr-1" style={{ color: '#948979' }}></i>
                          <strong>Notes:</strong> {rental.notes}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rental Amount */}
                  <div className="text-right">
                    <div className="text-2xl font-bold mb-1" style={{ color: '#948979' }}>
                      ₹{rental.amount}
                    </div>
                    <div className="text-sm font-medium" style={{ color: '#393E46' }}>
                      Rental Fee
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coming Soon Features */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
            <i className="fas fa-rocket text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>Coming Soon</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 rounded-2xl border-2 border-dashed" style={{ borderColor: '#DFD0B8' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-chart-line text-2xl" style={{ color: '#948979' }}></i>
            </div>
            <h4 className="font-bold mb-2" style={{ color: '#222831' }}>Rental Analytics</h4>
            <p className="text-sm" style={{ color: '#393E46' }}>Detailed rental performance metrics</p>
          </div>
          <div className="text-center p-6 rounded-2xl border-2 border-dashed" style={{ borderColor: '#DFD0B8' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-bell text-2xl" style={{ color: '#948979' }}></i>
            </div>
            <h4 className="font-bold mb-2" style={{ color: '#222831' }}>Rental Alerts</h4>
            <p className="text-sm" style={{ color: '#393E46' }}>Automated notifications for overdue items</p>
          </div>
          <div className="text-center p-6 rounded-2xl border-2 border-dashed" style={{ borderColor: '#DFD0B8' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-star text-2xl" style={{ color: '#948979' }}></i>
            </div>
            <h4 className="font-bold mb-2" style={{ color: '#222831' }}>Rating System</h4>
            <p className="text-sm" style={{ color: '#393E46' }}>User ratings and reviews for rentals</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RentalActivity;